import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "../config";
import apiClient from "../api/ResturantClient";

const SubscriptionPage = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch plans
        const plansResponse = await axios.get(
          `${config.API_BASE_URL}/subscription/plans`
        );
        setPlans(plansResponse.data);

        // Fetch subscription status if user is logged in
        const userId = sessionStorage.getItem("user_id");
        if (userId) {
          const statusResponse = await apiClient.get(
            `/subscription/subscription-status/${userId}`
          );
          setSubscriptionStatus(statusResponse.data);
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setSubscriptionStatus(null);
        } else {
          console.error("Error fetching subscription status", error);
          setMessage("Error loading subscription data. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubscribe = (plan) => {
    navigate("/payment-details", {
      state: {
        planDetails: plan,
      },
    });
  };

  const handleCancel = async () => {
    try {
      const userId = sessionStorage.getItem("user_id");
      const response = await apiClient.post(
        `/subscription/cancel-subscription/${userId}`
      );

      if (response.data.success) {
        let message = `${response.data.message}. `;

        if (response.data.refund_amount > 0) {
          message += `You've used ${response.data.used_days} days of your ${
            response.data.used_days + response.data.remaining_days
          } day subscription. `;
          message += `A pro-rated refund of $${response.data.refund_amount.toFixed(
            2
          )} `;
          message += `(${
            response.data.remaining_days
          } days × $${response.data.daily_rate.toFixed(2)}/day) `;
          message += `has been initiated.`;
        } else {
          message += `No refund issued as you've used all ${response.data.used_days} days of your subscription.`;
        }

        setMessage(message);
        setSubscriptionStatus({ status: "canceled" });

        setTimeout(() => {
          setMessage(
            (prev) => prev + " Refund may take 5-10 business days to process."
          );
        }, 3000);
      } else {
        setMessage(response.data.message || "Error canceling subscription");
      }
    } catch (error) {
      console.error("Error canceling subscription", error);
      let errorMsg = "Error canceling subscription. Please try again.";

      if (error.response) {
        if (error.response.data && error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (error.response.status === 402) {
          errorMsg = "Payment failed. Please update your payment method.";
        }
      }

      setMessage(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-900">Loading subscription plans...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="bg-white rounded-full p-3 shadow-lg inline-flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select the subscription plan that best fits your restaurant's needs
          </p>
        </div>

        {/* Notification */}
        {message && (
          <div className="max-w-4xl mx-auto mb-8 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-lg shadow-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-blue-700 text-sm font-medium">{message}</p>
            </div>
          </div>
        )}

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={plan.plan_id}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-105 ${
                index === 1 ? "ring-2 ring-purple-500 ring-opacity-50 relative" : ""
              }`}
            >
              {index === 1 && (
                <div className="absolute top-0 right-0 bg-purple-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                  Most Popular
                </div>
              )}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {plan.name}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {plan.description}
                </p>
                <div className="flex items-baseline mb-6">
                  <span className="text-4xl font-extrabold text-gray-900">
                    ${plan.price}
                  </span>
                  {plan.interval && (
                    <span className="ml-2 text-gray-500">/{plan.interval}</span>
                  )}
                </div>
                <button
                  onClick={() => handleSubscribe(plan)}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg px-6 py-4 font-semibold hover:from-blue-700 hover:to-purple-700 hover:shadow-lg transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50"
                >
                  Get Started
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Active Subscription Section */}
        {subscriptionStatus && subscriptionStatus.status === "active" && (
          <div className="max-w-4xl mx-auto mt-12 bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Your Active Subscription
                </h3>
                <p className="text-gray-600">
                  You're currently subscribed to our service
                </p>
              </div>
              <button
                onClick={handleCancel}
                className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg px-6 py-3 font-semibold hover:from-red-600 hover:to-red-700 hover:shadow-lg transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-red-300 focus:ring-opacity-50"
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="max-w-4xl mx-auto mt-16 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            All Plans Include
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              "24/7 Customer Support",
              "Secure Payment Processing",
              "Easy Cancellation",
              "Mobile Responsive",
              "Regular Updates",
              "Data Backup"
            ].map((feature, index) => (
              <div key={index} className="flex items-center justify-center space-x-2 text-gray-700">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;